import * as types from '../mutation-types';
import accounts from '../../api/accounts';
import user from '../../api/user';
import _ from 'lodash';

const state = {
  accounts: [
    {
      id: null
    }
  ],
  selected: {
    id: null,
    address: {},
    businesses: [
      {
        id: null,
        type: {}
      }
    ],
    users: [
      {
        id: null
      }
    ]
  },
  currentPage: 1,
  pageSize: 20,
  lastPage: 1,
  total: null,
  orderBy: 'name.raw',
  orderDirection: 'asc',
  search: '',
  filters: [],
  loadingAccounts: false
};

const getters = {
  AccountsList: state => state.accounts,
  SelectedAccount: state => state.selected,
  AccountSearch: state => state.search,
  AccountFilters: state => state.filters,
  AccountsTotal: state => state.total,
  AccountsCurrentPage: state => state.currentPage,
  AccountsLastPage: state => state.lastPage
};

const userUpdatedNotification = {
  text: 'User permissions have been updated',
  bottom: true,
  left: true,
  colour: 'success'
};

const actions = {
  getAccountsList({ commit, state }, append = false) {
    if (append) {
      commit(types.SET_ACCOUNTS_CURRENT_PAGE, state.currentPage + 1);
    } else {
      commit(types.SET_ACCOUNTS_CURRENT_PAGE, 1);
      commit(types.RESET_ACCOUNTS_TOTAL);
    }

    return new Promise((resolve, reject) => {
      if (append && state.lastPage === 1) {
        resolve(true);
      }

      const page = state.currentPage;
      const orderBy = [{ [state.orderBy]: state.orderDirection }];
      const dates = state.filters.dates;
      const search = _.isNull(state.search) ? '*' : state.search;

      const fields = ['*'];

      const terms = {};
      if (state.filters.length > 0) {
        terms['nested.businesses.type.name'] = state.filters;
      }

      accounts
        .getAccountsList(page, state.pageSize, orderBy, search, terms, dates, fields)
        .then(response => {
          if (append) {
            commit(types.APPEND_ACCOUNTS_PAGE, response);
          } else {
            commit(types.SET_ACCOUNTS_PAGE, response);
          }
          resolve(state.currentPage === state.lastPage);
        })
        .catch(error => {
          console.log(error.data);
          reject(error.data);
        });
    });
  },

  selectAccount({ commit }, id) {
    commit(types.SET_SELECTED_ACCOUNT, id);
  },

  updateAccount({ commit, dispatch }, { id, updates }) {
    accounts.update(id, updates).then(account => {
      dispatch('addNotification', {
        text: 'Account details have been updated',
        bottom: true,
        left: true,
        colour: 'success'
      });
      commit(types.SET_SELECTED_ACCOUNT_IN_LIST, account.data);
    });
  },

  registerAccountBusiness({ commit }, details) {
    return new Promise((resolve, reject) => {
      accounts
        .register(details)
        .then(() => {
          accounts.getAccount(details.account_id).then(account => {
            commit(types.SET_SELECTED_ACCOUNT_IN_LIST, account._source);
          });
          resolve();
        })
        .catch(error => reject(error));
    });
  },

  setAccountSearch({ commit }, search) {
    commit(types.SET_ACCOUNT_SEARCH, search);
  },
  addAccountFilter({ commit }, businessType) {
    commit(types.ADD_ACCOUNT_FILTER, businessType);
  },
  removeAccountFilter({ commit }, businessType) {
    commit(types.REMOVE_ACCOUNT_FILTER, businessType);
  },
  removeAllAccountFilters({ commit }) {
    commit(types.REMOVE_ALL_ACCOUNT_FILTERS);
  },

  updateUserBusinessRole({ commit, dispatch }, { id, updates }) {
    user.updateBusinessRole({ id, updates }).then(user => {
      dispatch('addNotification', userUpdatedNotification);
      commit(types.UPDATE_SELECTED_ACCOUNT_USER, user.data);
    });
  },

  updateUserSecurityRole({ commit, dispatch }, { id, updates }) {
    user.updateSecurityRole({ id, updates }).then(user => {
      dispatch('addNotification', userUpdatedNotification);
      commit(types.UPDATE_SELECTED_ACCOUNT_USER, user.data);
    });
  }
};

const mutations = {
  [types.SET_ACCOUNTS_PAGE](state, response) {
    state.accounts = _.map(response.hits, '_source');
    state.selected = state.accounts[0];
    state.total = response.total > 1000 ? 1000 : response.total;
    state.lastPage = Math.ceil(state.total / state.pageSize);
  },
  [types.APPEND_ACCOUNTS_PAGE](state, response) {
    state.accounts = [...state.accounts, ..._.map(response.hits, '_source')];
  },
  [types.SET_ACCOUNTS_CURRENT_PAGE](state, page) {
    state.currentPage = page;
  },
  [types.RESET_ACCOUNTS_TOTAL](state) {
    state.total = null;
  },
  [types.SET_SELECTED_ACCOUNT](state, id) {
    state.selected = _.find(state.accounts, ['id', id]);
  },
  [types.UPDATE_SELECTED_ACCOUNT](state, account) {
    state.selected = account;
  },
  [types.SET_ACCOUNT_SEARCH](state, search) {
    state.search = search;
  },
  [types.ADD_ACCOUNT_FILTER](state, businessType) {
    state.filters.push(businessType);
  },
  [types.REMOVE_ACCOUNT_FILTER](state, businessType) {
    state.filters = _.filter(state.filters, name => name !== businessType);
  },
  [types.REMOVE_ALL_ACCOUNT_FILTERS](state) {
    state.filters = [];
  },
  [types.SET_SELECTED_ACCOUNT_IN_LIST](state, response) {
    state.selected = response;
    const index = _.findIndex(state.accounts, ['id', response.id]);
    if (index >= 0) {
      state.accounts[index] = response;
    }
  },
  [types.RESET_ACCOUNT_LIST](state) {
    state.accounts = [
      {
        id: null
      }
    ];
    state.selected = {
      id: null,
      businesses: [
        {
          id: null
        }
      ],
      users: [
        {
          id: null
        }
      ]
    };
  },
  [types.START_LOADING_ACCOUNTS](state) {
    state.loadingAccounts = true;
  },
  [types.STOP_LOADING_ACCOUNTS](state) {
    state.loadingAccounts = false;
  },
  [types.UPDATE_SELECTED_ACCOUNT_USER](state, user) {
    let index = _.findIndex(state.selected.users, record => record.id === user.id);
    state.selected.users[index] = user;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
